// src/components/admin/ProductManagement.js
import React, { useState, useEffect, useCallback, useRef } from "react";
import { PaginatedList } from "./PaginatedList";
import { Trash2Icon, PencilIcon, PackagePlusIcon } from "lucide-react";
import { SearchIcon, FilterIcon } from "lucide-react";

const ProductManagement = ({
  setNotification,
  setActiveMenu,
  setConfirmModal,
}) => {
  const { useEffect } = React;
  const [showEditForm, setShowEditForm] = useState(false);
  const [pricingTier, setPricingTier] = useState({
    minQuantity: "",
    maxQuantity: "",
    price: "",
  });
  const [selectedItem, setSelectedItem] = useState(null);

  // New state variables for data management
  const [productsData, setProductsData] = useState([]);
  const [categoriesData, setCategoriesData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  //lazy img & pagination backedn
  const [productImages, setProductImages] = useState({});
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 5,
    total: 0,
    totalPages: 0,
  });
  const [filters, setFilters] = useState({
    search: "",
    category: "",
    sortBy: "createdAt",
    sortOrder: "desc",
  });
  const imageLoadingRef = useRef(new Set());
  const imageObserver = useRef(null);

  // Create a new state for form data
  const [formData, setFormData] = useState({
    name: "",
    category: "",
    basePrice: "",
    description: "",
    note: "",
    processingTime: "",
    images: [],
    hasGST: false,
    hasPST: false,
    pricingTiers: [],
    minimumOrder: 1,
    sku: "",
    regularPrice: "",
    upc: "",
    weight: "",
    dimensions: {},
    metadata: {
      searchTags: [],
    },
    isFeatured: false,
    inStock: true,
  });

  const initializeObserver = () => {
    console.log("Initializing new IntersectionObserver");

    // Disconnect any existing observer
    if (imageObserver.current) {
      imageObserver.current.disconnect();
    }

    // Create a new observer
    imageObserver.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const productId = entry.target.dataset.productId;
            if (productId && !imageLoadingRef.current.has(productId)) {
              console.log(`Loading image for product ${productId}`);
              loadProductImage(productId);
              imageLoadingRef.current.add(productId);
            }
          }
        });
      },
      { rootMargin: "100px", threshold: 0.1 }
    );
  };

  useEffect(() => {
    imageObserver.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const productId = entry.target.dataset.productId;
            if (productId && !imageLoadingRef.current.has(productId)) {
              loadProductImage(productId);
              imageLoadingRef.current.add(productId);
            }
          }
        });
      },
      { rootMargin: "100px", threshold: 0.1 }
    );

    return () => {
      if (imageObserver.current) {
        imageObserver.current.disconnect();
      }
    };
  }, []);

  const observeProductCards = () => {
    console.log("Starting to observe product cards");

    // Always re-initialize the observer to ensure it's fresh
    initializeObserver();

    // Find all product cards needing images
    setTimeout(() => {
      const productCards = document.querySelectorAll(
        ".product-image-container"
      );
      console.log(`Found ${productCards.length} product cards to observe`);

      if (productCards.length === 0) {
        console.log("No product cards found, will try again");
        setTimeout(observeProductCards, 500); // Try again if no cards found
        return;
      }

      // Start observing each card
      productCards.forEach((card) => {
        const productId = card.dataset.productId;
        if (productId) {
          imageObserver.current.observe(card);
          console.log(`Now observing card for product ${productId}`);
        } else {
          console.log("Found card without product ID");
        }
      });
    }, 100); // Short delay to ensure DOM is updated
  };

  useEffect(() => {
    if (!isLoading) {
      setTimeout(observeProductCards, 100);
    }
  }, [isLoading, productsData]);

  const loadProductImage = async (productId) => {
    try {
      console.log(`Starting image load for product ${productId}`);

      // Check if we already have this image
      if (productImages[productId]) {
        console.log(`Image for product ${productId} already loaded`);
        return;
      }

      const token = localStorage.getItem("token");
      const response = await fetch(`/api/products/${productId}/primary-image`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error(`Failed to load image: ${response.status}`);
      }

      const imageData = await response.json();
      console.log(`Successfully loaded image for product ${productId}`);

      setProductImages((prev) => ({
        ...prev,
        [productId]:
          imageData.url || imageData.path
            ? `${imageData.url || imageData.path}`
            : imageData.data,
      }));
    } catch (error) {
      console.error(`Error loading image for product ${productId}:`, error);
    }
  };

  const fetchProducts = useCallback(async () => {
    try {
      setIsLoading(true);

      console.log(`Fetching products for page ${pagination.page}`);

      // Important: Clear image cache when fetching new page
      setProductImages({});
      imageLoadingRef.current = new Set();

      const token = localStorage.getItem("token");

      const queryParams = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        search: filters.search,
        category: filters.category,
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder,
      });

      console.log("Fetching products with params:", queryParams.toString());

      const response = await fetch(`/api/products/paginated?${queryParams}`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log(
        `Received ${data.products.length} products for page ${pagination.page}`
      );

      setProductsData(data.products);
      setPagination({
        ...pagination,
        total: data.total,
        totalPages: data.totalPages,
      });
    } catch (error) {
      console.error("Error fetching products:", error);
      setNotification({
        type: "error",
        message: "Failed to load products. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  }, [pagination.page, pagination.limit, filters, setNotification]);

  // Add this useEffect to trigger fetchProducts when pagination changes
  useEffect(() => {
    fetchProducts();
  }, [pagination.page, fetchProducts]);

  const fetchProductDetails = async (productId) => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem("token");
      const response = await fetch(`/api/products/${productId}/details`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Error fetching product details:", error);
      setNotification({
        type: "error",
        message: "Failed to load product details.",
      });
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!isLoading && productsData.length > 0) {
      console.log(
        `Page ${pagination.page} loaded with ${productsData.length} products`
      );

      // Clear existing observation and start fresh
      if (imageObserver.current) {
        imageObserver.current.disconnect();
      }

      // Wait for the next render cycle
      setTimeout(() => {
        observeProductCards();
      }, 200);
    }
  }, [productsData, isLoading, pagination.page]);

  useEffect(() => {
    return () => {
      if (imageObserver.current) {
        console.log("Cleanup: disconnecting observer");
        imageObserver.current.disconnect();
      }
    };
  }, []);

  // Filter and pagination handlers
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({
      ...filters,
      [name]: value,
    });
  };

  const applyFilters = (e) => {
    e.preventDefault();
    setPagination({
      ...pagination,
      page: 1,
    });
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > pagination.totalPages) return;

    console.log(`Changing to page ${newPage}`);

    // Clear everything when changing pages
    setProductImages({});
    if (imageObserver.current) {
      imageObserver.current.disconnect();
      imageObserver.current = null;
    }
    imageLoadingRef.current = new Set();

    setPagination((prev) => ({
      ...prev,
      page: newPage,
    }));
  };

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch categories and products in parallel
      const [categoriesResponse, productsResponse] = await Promise.all([
        fetch("/api/categories", {
          method: "GET",
          headers: headers,
        }),
        fetch("/api/products", {
          method: "GET",
          headers: headers,
        }),
      ]);

      if (!categoriesResponse.ok) {
        throw new Error(`HTTP error! status: ${categoriesResponse.status}`);
      }

      if (!productsResponse.ok) {
        throw new Error(`HTTP error! status: ${productsResponse.status}`);
      }

      const fetchedCategories = await categoriesResponse.json();
      const fetchedProducts = await productsResponse.json();

      setCategoriesData(fetchedCategories);
      setProductsData(fetchedProducts);
    } catch (error) {
      console.error("Error fetching data:", error);
      setNotification({
        type: "error",
        message: "Failed to load data. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  }, [setNotification]);

  useEffect(() => {
    // Fetch categories only once
    const fetchCategories = async () => {
      try {
        const token = localStorage.getItem("token");
        const headers = { Authorization: `Bearer ${token}` };

        const categoriesResponse = await fetch("/api/categories", {
          method: "GET",
          headers: headers,
        });

        if (!categoriesResponse.ok) {
          throw new Error(`HTTP error! status: ${categoriesResponse.status}`);
        }

        const fetchedCategories = await categoriesResponse.json();
        setCategoriesData(fetchedCategories);
      } catch (error) {
        console.error("Error fetching categories:", error);
        setNotification({
          type: "error",
          message: "Failed to load categories. Please try again.",
        });
      }
    };

    // Now call fetchCategories and fetchProducts separately
    fetchCategories();
    fetchProducts();
  }, []);

  useEffect(() => {
    setShowEditForm(false);
    setFormData({
      name: "",
      category: "",
      basePrice: "",
      description: "",
      note: "",
      processingTime: "",
      images: [],
      hasGST: false,
      hasPST: false,
      pricingTiers: [],
      minimumOrder: 1,
      sku: "",
      regularPrice: "",
      upc: "",
      weight: "",
      dimensions: {},
      metadata: {
        searchTags: [],
      },
      isFeatured: false,
      inStock: true,
    });
  }, [productsData.length]);

  // Function to handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("token");
      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };

      // Make a deep copy and clean up the data
      const productData = {
        name: formData.name,
        category: formData.category,
        basePrice: parseFloat(formData.basePrice) || 0,
        description: formData.description || "",
        note: formData.note || "",
        processingTime: formData.processingTime || "",
        hasGST: !!formData.hasGST,
        hasPST: !!formData.hasPST,
        isFeatured: !!formData.isFeatured,
        inStock: !!formData.inStock,
        minimumOrder: parseInt(formData.minimumOrder) || 1,
        sku: formData.sku || "",
        regularPrice: parseFloat(formData.regularPrice) || 0,
        upc: formData.upc || "",
        weight: parseFloat(formData.basePrice) || 0,
        dimensions: formData.dimensions || {},
        metadata: {
          searchTags: Array.isArray(formData.metadata?.searchTags)
            ? formData.metadata.searchTags
            : [],
        },
      };

      // Handle pricing tiers (make sure they're all valid objects with numbers)
      if (Array.isArray(formData.pricingTiers)) {
        productData.pricingTiers = formData.pricingTiers.map((tier) => ({
          minQuantity: parseInt(tier.minQuantity) || 0,
          maxQuantity: tier.maxQuantity ? parseInt(tier.maxQuantity) : null,
          price: parseFloat(tier.price) || 0,
        }));
      } else {
        productData.pricingTiers = [];
      }

      // Handle images properly
      if (Array.isArray(formData.images)) {
        // Only send images that are strings and start with data:
        productData.images = formData.images.filter(
          (img) => typeof img === "string" && img.startsWith("data:")
        );
      } else {
        productData.images = [];
      }

      // Log what we're sending
      console.log(
        "Sending product data to server:",
        JSON.stringify({
          ...productData,
          images: productData.images.length + " images", // Don't log the full image data
        })
      );

      let response;

      if (selectedItem) {
        response = await fetch(`/api/products/${selectedItem._id}`, {
          method: "PUT",
          headers,
          body: JSON.stringify(productData),
        });
      } else {
        response = await fetch("/api/products", {
          method: "POST",
          headers,
          body: JSON.stringify(productData),
        });
      }

      // Handle error response
      if (!response.ok) {
        const errorData = await response.json();
        console.error("Server returned error:", errorData);
        throw new Error(
          errorData.error ||
            errorData.details ||
            `Failed to ${selectedItem ? "update" : "create"} product`
        );
      }

      const responseData = await response.json();

      await fetchData(); // Refresh data after submission
      setFormData({
        name: "",
        category: "",
        basePrice: "",
        description: "",
        note: "",
        processingTime: "",
        images: [],
        hasGST: false,
        hasPST: false,
        pricingTiers: [],
        minimumOrder: 1,
        sku: "",
        regularPrice: "",
        upc: "",
        weight: "",
        dimensions: {},
        metadata: {
          searchTags: [],
        },
      });
      setSelectedItem(null);
      setShowEditForm(false);

      setNotification({
        type: "success",
        message: selectedItem
          ? "Product updated successfully!"
          : "Product added successfully!",
      });

      setTimeout(() => setNotification(null), 3000);
    } catch (error) {
      console.error(
        `Error ${selectedItem ? "updating" : "creating"} product:`,
        error
      );
      setNotification({
        type: "error",
        message:
          error.message ||
          `Failed to ${selectedItem ? "update" : "add"} product`,
      });

      setTimeout(() => setNotification(null), 3000);
    }
  };
  // Handle deletion of a product
  const handleDelete = async (productId) => {
    const product = productsData.find((p) => p._id === productId);

    setConfirmModal({
      show: true,
      title: "Delete Product",
      message: `Are you sure you want to delete "${product?.name}"? This action cannot be undone.`,
      onConfirm: async () => {
        try {
          const token = localStorage.getItem("token");
          const response = await fetch(`/api/products/${productId}`, {
            method: "DELETE",
            headers: { Authorization: `Bearer ${token}` },
          });

          const data = await response.json();

          if (!response.ok) {
            throw new Error(data.error || `Failed to delete product`);
          }

          await fetchData(); // Refresh data after deletion

          setNotification({
            type: "success",
            message: data.message || `Product deleted successfully!`,
          });

          setTimeout(() => setNotification(null), 3000);
        } catch (error) {
          console.error(`Error deleting product:`, error);

          setNotification({
            type: "error",
            message: error.message || `Failed to delete product`,
          });

          setTimeout(() => setNotification(null), 3000);
        }
      },
    });
  };

  // Function to switch to Add Product mode
  const handleAddProductClick = () => {
    setShowEditForm(true);
    setFormData({
      name: "",
      category: "",
      basePrice: "",
      description: "",
      note: "",
      processingTime: "",
      images: [],
      hasGST: false,
      hasPST: false,
      pricingTiers: [],
      minimumOrder: 1,
      sku: "",
      regularPrice: "",
      upc: "",
      weight: "",
      dimensions: {},
      metadata: {
        searchTags: [],
      },
      isFeatured: false,
      inStock: true,
    });
    setSelectedItem(null);
  };

  const addPricingTier = () => {
    if (!pricingTier.minQuantity || !pricingTier.price) {
      alert("Please enter both minimum quantity and price");
      return;
    }

    const minQty = parseInt(pricingTier.minQuantity);
    const maxQty = pricingTier.maxQuantity
      ? parseInt(pricingTier.maxQuantity)
      : null;
    const price = parseFloat(pricingTier.price);

    // Validate numbers
    if (isNaN(minQty) || isNaN(price) || (maxQty !== null && isNaN(maxQty))) {
      alert("Please enter valid numbers");
      return;
    }

    if (minQty <= 0 || price <= 0 || (maxQty !== null && maxQty <= 0)) {
      alert("Please enter positive numbers");
      return;
    }

    if (maxQty && minQty >= maxQty) {
      alert("Maximum quantity must be greater than minimum quantity");
      return;
    }

    const newTiers = [...(formData.pricingTiers || [])];
    const hasOverlap = newTiers.some((tier) => {
      const tierMax = tier.maxQuantity || Infinity;
      const newMax = maxQty || Infinity;
      return minQty <= tierMax && tier.minQuantity <= newMax;
    });

    if (hasOverlap) {
      alert("Price tiers cannot have overlapping quantities");
      return;
    }

    newTiers.push({
      minQuantity: minQty,
      maxQuantity: maxQty,
      price: price,
    });

    newTiers.sort((a, b) => a.minQuantity - b.minQuantity);

    setFormData({ ...formData, pricingTiers: newTiers });
    setPricingTier({ minQuantity: "", maxQuantity: "", price: "" });
  };

  const removePricingTier = (index) => {
    const newTiers = [...(formData.pricingTiers || [])];
    newTiers.splice(index, 1);
    setFormData({ ...formData, pricingTiers: newTiers });
  };

  const handleImagesChange = (e, index) => {
    const files = Array.from(e.target.files);
    Promise.all(
      files.map((file) => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      })
    ).then((newImages) => {
      if (typeof index === "number") {
        const updatedImages = [...(formData.images || [])];
        updatedImages[index] = newImages[0];
        setFormData({ ...formData, images: updatedImages });
      } else {
        setFormData({
          ...formData,
          images: [...(formData.images || []), ...newImages],
        });
      }
    });
  };

  const removeImage = (index) => {
    const updatedImages = [...formData.images];
    updatedImages.splice(index, 1);
    setFormData({ ...formData, images: updatedImages });
  };

  const handleEdit = async (product) => {
    try {
      // Set loading state
      setIsLoading(true);

      // Await the product details
      const productDetails = await fetchProductDetails(product._id);

      // If product details couldn't be loaded, exit early
      if (!productDetails) {
        setIsLoading(false);
        return;
      }

      // Prepare images in the correct format
      let formattedImages = [];
      if (productDetails.images && Array.isArray(productDetails.images)) {
        formattedImages = productDetails.images
          .map((img) => {
            // Check if the image has a path property (new format)
            if (img.path) {
              return img.data || (img.url ? img.url : `/api${img.path}`);
            }
            // If it has data property (old format)
            if (img.data && typeof img.data === "string") {
              return img.data;
            }
            // Otherwise return null and filter it out below
            return null;
          })
          .filter((img) => img !== null);
      }

      // Create editable product from the full details
      const editableProduct = {
        ...productDetails,
        name: productDetails.name || "",
        category: productDetails.category?._id || productDetails.category || "",
        basePrice:
          typeof productDetails.basePrice === "number"
            ? productDetails.basePrice
            : 0,
        description: productDetails.description || "",
        note: productDetails.note || "",
        processingTime: productDetails.processingTime || "",
        images: formattedImages,
        hasGST: !!productDetails.hasGST,
        hasPST: !!productDetails.hasPST,
        pricingTiers: Array.isArray(productDetails.pricingTiers)
          ? productDetails.pricingTiers
          : [],
        minimumOrder:
          typeof productDetails.minimumOrder === "number"
            ? productDetails.minimumOrder
            : 1,
        sku: productDetails.sku || "",
        regularPrice:
          typeof productDetails.regularPrice === "number"
            ? productDetails.regularPrice
            : 0,
        upc: productDetails.upc || "",
        weight:
          typeof productDetails.weight === "number" ? productDetails.weight : 0,
        dimensions: productDetails.dimensions || {},
        metadata: productDetails.metadata || {
          searchTags: [],
        },
        isFeatured: !!productDetails.isFeatured,
        inStock: productDetails.inStock ?? true,
      };

      console.log("Editing product in form:", editableProduct);

      // Set the form data and selected item
      setFormData(editableProduct);
      setSelectedItem(productDetails);
      setShowEditForm(true);
      setIsLoading(false);
    } catch (error) {
      console.error("Error in edit product:", error);
      setNotification({
        type: "error",
        message: "Failed to load product details for editing",
      });
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 flex flex-col items-center justify-center">
        {/* <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" /> */}
        <img src="../images/loading.gif" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-2xl font-semibold">Product Management</h3>

        {/* Add Product button */}
        {!showEditForm && (
          <button
            onClick={handleAddProductClick}
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 flex items-center"
          >
            <PackagePlusIcon className="h-5 w-5 mr-2" />
            Add Product
          </button>
        )}
      </div>

      {/* <div>
       <h3 className="text-2xl font-bold mb-6">{isEdit ? 'Edit' : 'Add'} Product</h3> */}

      {!showEditForm && (
        <div>
          {productsData.map((product) => (
            <div
              key={product._id}
              className="flex justify-between items-center p-4 bg-white rounded-lg shadow mb-4"
            >
              <div
                className="w-24 h-24 relative product-image-container"
                data-product-id={product._id}
              >
                {productImages[product._id] ? (
                  <img
                    src={
                      productImages[product._id].startsWith("data:")
                        ? productImages[product._id]
                        : productImages[product._id]
                    }
                    alt={product.name}
                    className="w-full h-full object-cover rounded"
                  />
                ) : (
                  <div className="w-full h-full bg-gray-200 rounded flex items-center justify-center">
                    <span className="text-xs text-gray-500">Loading...</span>
                  </div>
                )}
              </div>

              <div className="flex-grow mx-4">
                <h4 className="font-semibold">{product.name}</h4>
                <p className="text-gray-600">
                  Category: {product.category?.name}
                </p>
                <p className="text-sm text-gray-500">
                  Created: {new Date(product.createdAt).toLocaleDateString()}
                </p>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => handleEdit(product)}
                  className="p-2 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded-full transition-colors"
                  title="Edit product"
                >
                  <PencilIcon className="h-5 w-5" />
                </button>
                <button
                  onClick={() => handleDelete(product._id)}
                  className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-full transition-colors"
                  title="Delete product"
                >
                  <Trash2Icon className="h-5 w-5" />
                </button>
              </div>
            </div>
          ))}

          {pagination.totalPages > 1 && (
            <div className="flex justify-center mt-6">
              <nav className="flex items-center space-x-2">
                <button
                  onClick={() => handlePageChange(1)}
                  disabled={pagination.page === 1}
                  className={`px-3 py-1 rounded ${
                    pagination.page === 1
                      ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  First
                </button>
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className={`px-3 py-1 rounded ${
                    pagination.page === 1
                      ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  Prev
                </button>

                <span className="px-3 py-1">
                  Page {pagination.page} of {pagination.totalPages}
                </span>

                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.totalPages}
                  className={`px-3 py-1 rounded ${
                    pagination.page === pagination.totalPages
                      ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  Next
                </button>
                <button
                  onClick={() => handlePageChange(pagination.totalPages)}
                  disabled={pagination.page === pagination.totalPages}
                  className={`px-3 py-1 rounded ${
                    pagination.page === pagination.totalPages
                      ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  Last
                </button>
              </nav>
            </div>
          )}
        </div>
      )}

      {showEditForm && (
        <form
          onSubmit={handleSubmit}
          className="space-y-4 bg-white p-6 rounded-lg shadow"
        >
          {showEditForm && (
            <div className="flex justify-between items-center mb-4">
              <h4 className="text-lg font-medium">
                {formData._id ? "Edit" : "Add"} Product
              </h4>
              <button
                type="button"
                onClick={() => {
                  setShowEditForm(false);
                  setSelectedItem(null);
                  setFormData({
                    name: "",
                    category: "",
                    basePrice: "",
                    description: "",
                    note: "",
                    processingTime: "",
                    images: [],
                    hasGST: false,
                    hasPST: false,
                    pricingTiers: [],
                    minimumOrder: 1,
                    sku: "",
                    regularPrice: "",
                    upc: "",
                    weight: "",
                    dimensions: {},
                    metadata: {
                      searchTags: [],
                    },
                    isFeatured: false,
                    inStock: true,
                  });
                }}
                className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
              >
                Cancel
              </button>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3  gap-4">
            <div>
              <label className="block text-gray-700 mb-2">Name</label>
              <input
                type="text"
                value={formData.name || ""}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                className="w-full px-3 py-2 border rounded"
                required
              />
            </div>

            <div>
              <label className="block text-gray-700 mb-2">Category</label>
              <select
                value={formData.category || ""}
                onChange={(e) =>
                  setFormData({ ...formData, category: e.target.value })
                }
                className="w-full px-3 py-2 border rounded"
                required
              >
                <option value="">Select Category</option>
                {categoriesData.map((category) => (
                  <option key={category._id} value={category._id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-gray-700 mb-2">Minimum Order</label>
              <input
                type="number"
                min="1"
                value={formData.minimumOrder || "1"}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    minimumOrder: parseInt(e.target.value),
                  })
                }
                className="w-full px-3 py-2 border rounded"
              />
            </div>

            <div>
              <label className="block text-gray-700 mb-2">
                Processing Time
              </label>
              <input
                type="text"
                value={formData.processingTime || ""}
                onChange={(e) =>
                  setFormData({ ...formData, processingTime: e.target.value })
                }
                className="w-full px-3 py-2 border rounded"
              />
            </div>

            <div>
              <label className="block text-gray-700 mb-2">SKU</label>
              <input
                type="text"
                value={formData.sku || ""}
                onChange={(e) =>
                  setFormData({ ...formData, sku: e.target.value })
                }
                className="w-full px-3 py-2 border rounded"
              />
            </div>

            <div className="mb-0">
              <label className="block text-gray-700 mb-2">UPC</label>
              <input
                type="text"
                value={formData.upc || ""}
                onChange={(e) =>
                  setFormData({ ...formData, upc: e.target.value })
                }
                className="w-full px-3 py-2 border rounded"
              />
            </div>
          </div>

          <div>
            <label className="block text-gray-700 mb-2">Description</label>
            <textarea
              value={formData.description || ""}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              className="w-full px-3 py-2 border rounded"
              rows="3"
            />
          </div>

          <div>
            <label className="block text-gray-700 mb-2">Note</label>
            <textarea
              value={formData.note || ""}
              onChange={(e) =>
                setFormData({ ...formData, note: e.target.value })
              }
              className="w-full px-3 py-2 border rounded mb-4"
            />
          </div>

          <div className="pt-5">
            <h4 className="font-bold">Measures:</h4>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-gray-700 mb-2">Weight (g)</label>
              <input
                type="number"
                step="0.1"
                value={formData.weight || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    weight: parseFloat(e.target.value),
                  })
                }
                className="w-full px-3 py-2 border rounded"
              />
            </div>
            <div>
              <label className="block text-gray-700 mb-2">Length (inch)</label>
              <input
                type="number"
                step="0.1"
                value={formData.dimensions?.length || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    dimensions: {
                      ...(formData.dimensions || {}),
                      length: parseFloat(e.target.value),
                    },
                  })
                }
                className="w-full px-3 py-2 border rounded"
              />
            </div>
            <div>
              <label className="block text-gray-700 mb-2">Width (inch)</label>
              <input
                type="number"
                step="0.1"
                value={formData.dimensions?.width || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    dimensions: {
                      ...(formData.dimensions || {}),
                      width: parseFloat(e.target.value),
                    },
                  })
                }
                className="w-full px-3 py-2 border rounded"
              />
            </div>
            <div>
              <label className="block text-gray-700 mb-2">Height (inch)</label>
              <input
                type="number"
                step="0.1"
                value={formData.dimensions?.height || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    dimensions: {
                      ...(formData.dimensions || {}),
                      height: parseFloat(e.target.value),
                    },
                  })
                }
                className="w-full px-3 py-2 border rounded"
              />
            </div>
          </div>

          <div className="pt-8">
            <h4 className="font-bold">Pricing:</h4>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
            {/* Base Price Section - Required if no pricing tiers - basePrice */}
            {/* mayjor calculation would be based on this price  */}
            <div className="mb-0">
              <label className="block text-gray-700 mb-2">
                {" "}
                Wholesale Price (B2B)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.basePrice || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    basePrice: parseFloat(e.target.value),
                  })
                }
                className="w-full px-3 py-2 border rounded"
                // required={!formData.pricingTiers?.length} // Only required if no pricing tiers
              />
              <p className="text-sm text-gray-500 mt-1">
                {formData.pricingTiers?.length > 0
                  ? "Optional when using price tiers"
                  : "Required when not using price tiers or Regular price"}
              </p>
            </div>

            <div className="mb-0">
              <label className="block text-gray-700 mb-2">
                Regular Price(B2C)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.regularPrice || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    regularPrice: parseFloat(e.target.value),
                  })
                }
                className="w-full px-3 py-2 border rounded"
                // required={!formData.pricingTiers?.length} // Only required if no pricing tiers
              />
              <p className="text-sm text-gray-500 mt-1">
                {formData.pricingTiers?.length > 0
                  ? "Optional when using price tiers"
                  : "Required when not using price tiers or base price"}
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <p>Price Tiers</p>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <input
                type="number"
                placeholder="Min Quantity"
                value={pricingTier.minQuantity}
                onChange={(e) =>
                  setPricingTier({
                    ...pricingTier,
                    minQuantity: e.target.value,
                  })
                }
                className="px-3 py-2 border rounded"
              />
              <input
                type="number"
                placeholder="Max Quantity (optional)"
                value={pricingTier.maxQuantity}
                onChange={(e) =>
                  setPricingTier({
                    ...pricingTier,
                    maxQuantity: e.target.value,
                  })
                }
                className="px-3 py-2 border rounded"
              />
              <input
                type="number"
                step="0.01"
                placeholder="Price per unit"
                value={pricingTier.price}
                onChange={(e) =>
                  setPricingTier({ ...pricingTier, price: e.target.value })
                }
                className="px-3 py-2 border rounded"
              />
              <button
                type="button"
                onClick={addPricingTier}
                className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
              >
                Add Tier
              </button>
            </div>

            <div className="mt-4">
              {formData.pricingTiers?.map((tier, index) => (
                <div
                  key={index}
                  className="flex items-center space-x-2 mb-2 bg-gray-50 p-2 rounded"
                >
                  <span>
                    {tier.minQuantity} - {tier.maxQuantity || "∞"} units: $
                    {tier.price} each
                  </span>
                  <button
                    type="button"
                    onClick={() => removePricingTier(index)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <Trash2Icon className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="pt-6 space-y-8">
            <div>
              <label className="block text-gray-700 mb-2">
                Search Tags (comma-separated)
              </label>
              <input
                type="text"
                value={formData.metadata?.searchTags?.join(", ") || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    metadata: {
                      ...(formData.metadata || {}),
                      searchTags: e.target.value
                        .split(",")
                        .map((tag) => tag.trim()),
                    },
                  })
                }
                className="w-full px-3 py-2 border rounded"
                placeholder="tag1, tag2, tag3"
              />
            </div>

            <div className="flex space-x-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.hasGST || false}
                  onChange={(e) =>
                    setFormData({ ...formData, hasGST: e.target.checked })
                  }
                  className="mr-2"
                />
                <label>GST</label>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.hasPST || false}
                  onChange={(e) =>
                    setFormData({ ...formData, hasPST: e.target.checked })
                  }
                  className="mr-2"
                />
                <label>PST</label>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.isFeatured || false}
                  onChange={(e) =>
                    setFormData({ ...formData, isFeatured: e.target.checked })
                  }
                  className="mr-2"
                />
                <label>Featured</label>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.inStock || false}
                  onChange={(e) =>
                    setFormData({ ...formData, inStock: e.target.checked })
                  }
                  className="mr-2"
                />
                <label>In Stock</label>
              </div>
            </div>

            <div className="space-y-4">
              <label className="block text-gray-700">
                Add New Images
                <input
                  type="file"
                  onChange={(e) => handleImagesChange(e)}
                  accept="image/*"
                  multiple
                  className="mt-1 w-full"
                />
              </label>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {formData.images?.map((image, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={
                        typeof image === "string"
                          ? image
                          : image.path
                          ? image.path.startsWith("/api")
                            ? image.path
                            : `/api${image.path}`
                          : image.data || ""
                      }
                      alt={`Product ${index + 1}`}
                      className="w-full h-32 object-cover rounded border"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center space-x-2">
                      <input
                        type="file"
                        onChange={(e) => handleImagesChange(e, index)}
                        accept="image/*"
                        className="absolute inset-0 opacity-0 cursor-pointer"
                        title="Replace image"
                      />
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="relative z-10 bg-red-500 text-white p-2 rounded-full hover:bg-red-600"
                        title="Remove image"
                      >
                        ×
                      </button>
                    </div>
                    {index === 0 && (
                      <span className="absolute top-0 left-0 bg-blue-500 text-white px-2 py-1 text-xs rounded-br">
                        Primary
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <button
            type="submit"
            className="w-full bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
          >
            {formData._id ? "Update" : "Add"} Product
          </button>
        </form>
      )}
    </div>
  );
};

export default ProductManagement;
